/**
 * Catálogo maestro de buceo - une todos los cursos y especialidades
 * Tipo PADI/SSI con niveles de certificación
 */
import { openWaterCourse } from './openWater'
import { advancedOpenWaterCourse } from './advancedOpenWater'
import { divingSpecialties, type DivingSpecialty } from './divingSpecialties'
import type { Course } from './openWater'

// ─── Niveles de certificación ────────────────────────────────────
export interface CertificationLevel {
  id: string
  name: string
  nameEn: string
  emoji: string
  description: string
  descriptionEn: string
  color: string
  gradient: string
  /** Profundidad máxima permitida */
  maxDepth: number
  /** Inmersiones mínimas para el nivel */
  minDives: number
  /** Cursos asociados */
  courses: string[]
  /** Especialidades recomendadas */
  recommendedSpecialties: string[]
  /** Imagen de búsqueda */
  imageQuery: string
}

export const certificationLevels: CertificationLevel[] = [
  {
    id: 'scuba-diver',
    name: 'Scuba Diver',
    nameEn: 'Scuba Diver',
    emoji: '🫧',
    description: 'Primer contacto con el buceo. Inmersiones supervisadas hasta 12m.',
    descriptionEn: 'First contact with diving. Supervised dives to 12m.',
    color: 'sky',
    gradient: 'from-sky-300 to-cyan-400',
    maxDepth: 12,
    minDives: 2,
    courses: [],
    recommendedSpecialties: [],
    imageQuery: 'beginner scuba diver first dive swimming pool',
  },
  {
    id: 'open-water',
    name: 'Open Water Diver',
    nameEn: 'Open Water Diver',
    emoji: '🤿',
    description: 'Buceador certificado. Inmersiones hasta 18m con buddy.',
    descriptionEn: 'Certified diver. Dives to 18m with buddy.',
    color: 'cyan',
    gradient: 'from-cyan-400 to-blue-500',
    maxDepth: 18,
    minDives: 4,
    courses: ['open-water'],
    recommendedSpecialties: ['spec-buoyancy', 'spec-nitrox', 'spec-navigation'],
    imageQuery: 'open water diver certification coral reef',
  },
  {
    id: 'advanced-open-water',
    name: 'Advanced Open Water Diver',
    nameEn: 'Advanced Open Water Diver',
    emoji: '🦈',
    description: 'Buceador avanzado. Inmersiones hasta 30m. 5 aventuras completadas.',
    descriptionEn: 'Advanced diver. Dives to 30m. 5 adventures completed.',
    color: 'indigo',
    gradient: 'from-indigo-500 to-purple-600',
    maxDepth: 30,
    minDives: 25,
    courses: ['open-water', 'advanced-open-water'],
    recommendedSpecialties: ['spec-deep', 'spec-night', 'spec-wreck', 'spec-photo'],
    imageQuery: 'advanced open water diver deep dive underwater',
  },
  {
    id: 'rescue-diver',
    name: 'Rescue Diver',
    nameEn: 'Rescue Diver',
    emoji: '🆘',
    description: 'Buceador de rescate. Prevención y gestión de emergencias.',
    descriptionEn: 'Rescue diver. Emergency prevention and management.',
    color: 'red',
    gradient: 'from-red-500 to-rose-600',
    maxDepth: 30,
    minDives: 40,
    courses: ['open-water', 'advanced-open-water'],
    recommendedSpecialties: ['spec-rescue', 'spec-search-recovery'],
    imageQuery: 'rescue diver saving unconscious diver underwater',
  },
  {
    id: 'master-diver',
    name: 'Master Diver',
    nameEn: 'Master Diver',
    emoji: '👑',
    description: 'Nivel más alto recreativo. 50+ inmersiones y 5+ especialidades.',
    descriptionEn: 'Highest recreational level. 50+ dives and 5+ specialties.',
    color: 'amber',
    gradient: 'from-amber-400 to-yellow-500',
    maxDepth: 40,
    minDives: 50,
    courses: ['open-water', 'advanced-open-water'],
    recommendedSpecialties: ['spec-deep', 'spec-rescue', 'spec-night', 'spec-wreck', 'spec-nitrox'],
    imageQuery: 'master scuba diver professional deep diving',
  },
]

// ─── Ruta de aprendizaje (Learning Path) ─────────────────────────
export interface LearningPathStep {
  type: 'course' | 'specialty' | 'certification'
  id: string
  name: string
  nameEn: string
  emoji: string
  description: string
  descriptionEn: string
  gradient: string
  /** true si el usuario lo ha completado */
  isCompleted?: boolean
}

export function getDivingLearningPath(): LearningPathStep[] {
  return [
    {
      type: 'course',
      id: 'open-water',
      name: 'Open Water Diver',
      nameEn: 'Open Water Diver',
      emoji: '🤿',
      description: 'Tu primera certificación de buceo',
      descriptionEn: 'Your first diving certification',
      gradient: 'from-cyan-400 to-blue-500',
    },
    {
      type: 'specialty',
      id: 'spec-buoyancy',
      name: 'Flotabilidad Perfecta',
      nameEn: 'Peak Performance Buoyancy',
      emoji: '⚖️',
      description: 'Domina tu movimiento bajo el agua',
      descriptionEn: 'Master your movement underwater',
      gradient: 'from-cyan-400 to-teal-500',
    },
    {
      type: 'specialty',
      id: 'spec-nitrox',
      name: 'Nitrox',
      nameEn: 'Enriched Air Nitrox',
      emoji: '🫧',
      description: 'Más tiempo de fondo con aire enriquecido',
      descriptionEn: 'More bottom time with enriched air',
      gradient: 'from-green-400 to-emerald-600',
    },
    {
      type: 'course',
      id: 'advanced-open-water',
      name: 'Advanced Open Water',
      nameEn: 'Advanced Open Water',
      emoji: '🦈',
      description: 'Flotabilidad, navegación, nocturna, profunda y corrientes',
      descriptionEn: 'Buoyancy, navigation, night, deep and currents',
      gradient: 'from-indigo-500 to-purple-600',
    },
    {
      type: 'specialty',
      id: 'spec-deep',
      name: 'Buceo Profundo',
      nameEn: 'Deep Diving',
      emoji: '🏊',
      description: 'Explora hasta 40 metros',
      descriptionEn: 'Explore to 40 meters',
      gradient: 'from-indigo-500 to-blue-700',
    },
    {
      type: 'specialty',
      id: 'spec-night',
      name: 'Buceo Nocturno',
      nameEn: 'Night Diving',
      emoji: '🌙',
      description: 'Descubre el arrecife de noche',
      descriptionEn: 'Discover the reef at night',
      gradient: 'from-purple-500 to-indigo-700',
    },
    {
      type: 'specialty',
      id: 'spec-wreck',
      name: 'Pecios',
      nameEn: 'Wreck Diving',
      emoji: '🚢',
      description: 'Explora barcos hundidos',
      descriptionEn: 'Explore sunken ships',
      gradient: 'from-slate-500 to-gray-700',
    },
    {
      type: 'specialty',
      id: 'spec-rescue',
      name: 'Rescate',
      nameEn: 'Rescue Diver',
      emoji: '🆘',
      description: 'Prevención y rescate de emergencias',
      descriptionEn: 'Emergency prevention and rescue',
      gradient: 'from-red-500 to-rose-600',
    },
    {
      type: 'certification',
      id: 'master-diver',
      name: 'Master Diver',
      nameEn: 'Master Diver',
      emoji: '👑',
      description: 'El nivel más alto del buceo recreativo',
      descriptionEn: 'The highest recreational diving level',
      gradient: 'from-amber-400 to-yellow-500',
    },
  ]
}

// ─── Helpers ─────────────────────────────────────────────────────
export const getCourseById = (id: string): Course | undefined => {
  if (id === 'open-water') return openWaterCourse
  if (id === 'advanced-open-water') return advancedOpenWaterCourse
  return undefined
}

export const getCertificationById = (id: string): CertificationLevel | undefined =>
  certificationLevels.find(c => c.id === id)

export const getSpecialtiesForLevel = (levelId: string): DivingSpecialty[] => {
  const level = getCertificationById(levelId)
  if (!level) return []
  return level.recommendedSpecialties
    .map(id => divingSpecialties.find(s => s.id === id))
    .filter(Boolean) as DivingSpecialty[]
}

// ─── Estadísticas del catálogo ───────────────────────────────────
export const catalogStats = {
  totalCourses: 2,
  totalSpecialties: divingSpecialties.length,
  totalCertificationLevels: certificationLevels.length,
  get totalQuestions() {
    // Lazy: evita importar todos los quiz modules
    return 75 + 80 + 25 // AOWD + Specialties + OW
  },
}
